import React from 'react';
import { HeadingSmall, HeadingMedium, HeadingLarge } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard } from '../../shared';

/**
 * The three configurator headings share one page: they are the same idea at
 * three scales, and seeing them together is how you pick the right one.
 */

const Frame = ({ children }) => (
  <div className="max-w-xl p-[var(--space-ui-medium-m)] bg-[var(--color-gray-025)] border border-[var(--color-gray-1)] rounded-[var(--radius-card)]">
    {children}
  </div>
);

export const meta = {
  title: 'Headings',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  description:
    'Three section headings for the product configurator. Small is a one-line row carrying the current selection and an add-on price; Medium is a plain title with an optional price; Large opens the page with the product name, price and description.',
  propSets: [
    {
      title: 'HeadingSmall',
      component: HeadingSmall,
      componentName: 'HeadingSmall',
      rows: [
        ['title', 'string', "'Title'", 'Section title (typo-h4)'],
        ['selection', 'string', '—', 'Current selection, rendered after the separator'],
        ['separator', 'string', "'/'", 'Glyph between title and selection — "•" in the M2 (Overall) headings'],
        ['price', 'string', '—', 'Right-aligned add-on price, e.g. "+49,-"'],
        ['extraInfo', 'string', '—', 'Muted extra note, e.g. a size equivalence'],
        ['showSelection', 'bool', 'true', 'Show the selection block and its info button'],
        ['showInfoButton', 'bool', 'true', 'Show the (?) button'],
        ['showExtraInfo', 'bool', 'true', 'Show the extra note'],
        ['infoTooltip', 'string', '—', 'Tooltip text on the (?) button'],
        ['onInfoClick', 'func', '—', '(?) click handler — usually opens a SidePanel'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
    {
      title: 'HeadingMedium',
      component: HeadingMedium,
      componentName: 'HeadingMedium',
      rows: [
        ['title', 'string', "'Title'", 'Section title (typo-h3)'],
        ['price', 'string', '—', 'Right-aligned price'],
        ['titleVariant', "'h3' | 'h2'", "'h3'", 'Title size (typo-h3 / typo-h2); h2 for a section that opens a column'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
    {
      title: 'HeadingLarge',
      component: HeadingLarge,
      componentName: 'HeadingLarge',
      rows: [
        ['subtitle', 'string', '—', "Short description above the title (typo-h5) — the product's BE \"Short Description\""],
        ['title', 'string', "'Title'", 'Product title (typo-h2-editorial)'],
        ['titleAs', "'h1' | 'h2' | 'h3'", "'h2'", 'Heading element of the title — "h1" on the product page; the look stays typo-h2-editorial'],
        ['price', 'string', '—', 'Price under the title'],
        ['description', 'node', '—', 'Description — accepts a node, so <br /> and markup work'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
  ],
  usage: [
    {
      title: 'A configurator section',
      code: `import { HeadingSmall, LabelSelector } from '@ajat/design-system/Configurator';

<div className="pp__section">
  <HeadingSmall
    title="Størrelse"
    selection={selectedSize}
    extraInfo="EU 38–40"
    price="+0,-"
    onInfoClick={() => setSizeGuideOpen(true)}
  />
  <div className="flex gap-[var(--space-ui-small-m)]">
    {sizes.map((s) => <LabelSelector key={s}>{s}</LabelSelector>)}
  </div>
</div>`,
    },
  ],
};

export default function HeadingsPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="HeadingSmall" surface={false}>
        <div className="flex flex-col gap-[var(--space-ui-medium-m)]">
          <Frame><HeadingSmall title="Størrelse" selection="MEDIUM" extraInfo="EU 38–40" price="+49,-" /></Frame>
          <Frame><HeadingSmall title="Farve" selection="SORT" showExtraInfo={false} price="+0,-" /></Frame>
          <Frame><HeadingSmall title="Stil" selection="SVART" separator="•" showExtraInfo={false} infoTooltip="Stilen avgör färg och material" /></Frame>
          <Frame><HeadingSmall title="Monogram" showSelection={false} showExtraInfo={false} /></Frame>
          <Frame><HeadingSmall title="Initialer" showSelection={false} showExtraInfo={false} price={null} /></Frame>
        </div>
      </DemoCard>

      <DemoCard title="HeadingMedium" surface={false}>
        <div className="flex flex-col gap-[var(--space-ui-medium-m)]">
          <Frame><HeadingMedium title="Ekstra tilbehør" price="+159,-" /></Frame>
          <Frame><HeadingMedium title="Vælg din pakke" /></Frame>
        </div>
      </DemoCard>

      <DemoCard title="HeadingLarge" surface={false}>
        <div className="flex flex-col gap-[var(--space-ui-medium-m)]">
          <Frame>
            <HeadingLarge
              title="Royal Hoodie"
              price="499,-"
              description={(
                <>
                  Denne hoodie i økologisk bomuld er en del af vores Royal Collection, holdbarheden er unik og
                  kvaliteten helt i top. C.L. Seiferts vision er at alle vores tøjprodukter er bæredygtige og
                  klimavenlige, så vi minimerer CO2 aftrykket på jorden.
                  <br /><br />
                  Den består af 80 % organisk bomuld og 20 % genbrugs polyester, og dermed et bæredygtigt produkt.
                </>
              )}
            />
          </Frame>
          <Frame><HeadingLarge title="Basic T-Shirt" price="249,-" /></Frame>
          <Frame><HeadingLarge title="Konfigurer dit produkt" /></Frame>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
